// React and related imports
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { observer } from "mobx-react";

// Third-party libraries
import { Button, Tooltip } from "antd";
import { CopyOutlined, BookOutlined, CommentOutlined } from "@ant-design/icons";

// App-specific imports
import format from "@app/lib/format";
import bookmarksStore from "@app/state/store/report/document/bookmarks";
import { default as BookmarkModal } from "@app/components/document/bookmark/modal";
import PulseCircle from "@app/components/pdf-viewer/plugins/highlight/pulse-circle";
import notify from "@app/components/notify";
import { default as CommentsModal } from "@app/components/comments/modal";

// Local imports
import BookmarkTab from "./bookmark-tab";
import CommentTab from "./comment-tab";

// Styles
import "./style/preview.scoped.scss";

const RenderBookmarksTabs = ({ bookmarks, document }) => {
    if (!bookmarks || !Array.isArray(bookmarks) || bookmarks.length === 0 || !document) {
        return null;
    }

    const filteredBookmarks = bookmarks.filter(
        (bookmark) => bookmark && bookmark.document === document._id,
    );

    if (filteredBookmarks.length === 0) {
        return null;
    }

    return (
        <>
            {filteredBookmarks.map((bookmark) => (
                <BookmarkTab key={bookmark._id} bookmark={bookmark} />
            ))}
        </>
    );
};

const RenderCommentsTabs = ({ comments, document }) => {
    if (!comments || !Array.isArray(comments) || comments.length === 0 || !document) {
        return null;
    }

    return (
        <>
            {comments.map((comment) => (
                <CommentTab key={comment._id} comment={comment} />
            ))}
        </>
    );
};

const HighlightTarget = ({
    left,
    top,
    showActions,
    onCopyLink,
    onAddAsBookmark,
    onAddAsComment,
}) => {
    const targetRef = useRef(null);

    useEffect(() => {
        if (!showActions && targetRef.current) {
            setTimeout(() => {
                if (targetRef.current) {
                    targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100);
        }
    }, [left, top, showActions, targetRef.current]);

    return (
        <div
            ref={targetRef}
            className={"target-container"}
            style={{
                left: `${left}%`,
                top: `${top}%`,
            }}
        >
            <PulseCircle style={{ top: 0, left: 0 }} />

            {showActions && (
                <div className="tooltip-container">
                    <Tooltip title={"Copy"} placement={"bottom"} mouseEnterDelay>
                        <Button type={"text"} icon={<CopyOutlined />} onClick={onCopyLink} />
                    </Tooltip>
                    {onAddAsBookmark && (
                        <Tooltip title={"Bookmark"} placement={"bottom"} mouseEnterDelay>
                            <Button
                                type={"text"}
                                onClick={onAddAsBookmark}
                                icon={<BookOutlined />}
                            />
                        </Tooltip>
                    )}
                    {onAddAsComment && (
                        <Tooltip title={"Comment"} placement={"bottom"} mouseEnterDelay>
                            <Button
                                type={"text"}
                                onClick={onAddAsComment}
                                icon={<CommentOutlined />}
                            />
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};

const useSetPositionFromUlr = ({
    bookmarks,
    setHighlightPosition,
    setShowTargetWithActions,
    comments,
}) => {
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const area = searchParams.get("area");
    const bid = searchParams.get("bid");
    const comment = searchParams.get("comment");

    useEffect(() => {
        if (area) {
            const parsedArea = JSON.parse(area);

            setHighlightPosition({
                left: parsedArea.left,
                top: parsedArea.top,
            });
            setShowTargetWithActions(false);
        }
        if (bid) {
            const selectedBookmark = bookmarks.find((b) => b._id === bid);
            if (selectedBookmark && selectedBookmark.areas && selectedBookmark.areas.length > 0) {
                setHighlightPosition({
                    left: selectedBookmark.areas[0].left,
                    top: selectedBookmark.areas[0].top,
                });
                setShowTargetWithActions(false);
            }
        }
        if (comment) {
            const selectedComment = comments.find((c) => c._id === comment);
            if (selectedComment && selectedComment.areas && selectedComment.areas.length > 0) {
                setHighlightPosition({
                    left: selectedComment.areas[0].left,
                    top: selectedComment.areas[0].top,
                });
                setShowTargetWithActions(false);
            }
        }
    }, [area, bid, setHighlightPosition, bookmarks, comment, comments]);
};

const ImagePreview = observer(({ file, document, onBookmark, onComment, comments }) => {
    const bookmarks = bookmarksStore?.bookmarks || [];
    const imgRef = useRef(null);
    const [showTargetWithActions, setShowTargetWithActions] = useState(false);
    const [highlightPosition, setHighlightPosition] = useState({});

    const [bookmark, setBookmark] = useState(null);
    const [comment, setComment] = useState(null);

    const location = useLocation();
    const history = useHistory();

    useSetPositionFromUlr({
        bookmarks,
        setHighlightPosition,
        setShowTargetWithActions,
        comments,
    });

    if (!file.isImage || !document.file?._id) {
        return null;
    }

    const onRightClick = useCallback(
        (e) => {
            e.preventDefault();

            const imageElement = e.target;
            const imageRect = imageElement.getBoundingClientRect();
            const x = e.clientX - imageRect.left;
            const y = e.clientY - imageRect.top;

            const relativeX = (x / imageRect.width) * 100;
            const relativeY = (y / imageRect.height) * 100;

            history.replace({
                pathname: location.pathname,
                search: "",
            });

            setHighlightPosition({
                left: relativeX,
                top: relativeY,
            });
            setShowTargetWithActions(true);
        },
        [location, history],
    );

    const onClick = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        if (!searchParams.get("area") && !searchParams.get("bid") && !bookmark) {
            setHighlightPosition({});
        }
        setShowTargetWithActions(false);
    }, [location.search, bookmark]);

    const onAddAsBookmark = () => {
        setBookmark({
            areas: [
                {
                    top: highlightPosition.top,
                    left: highlightPosition.left,
                    isPoint: true,
                },
            ],
        });
        setShowTargetWithActions(false);
    };

    const onAddAsComment = () => {
        setComment({
            areas: [
                {
                    top: highlightPosition.top,
                    left: highlightPosition.left,
                    isPoint: true,
                },
            ],
        });
    };

    const onCopyLink = async () => {
        const data = {};
        const selectedText = "";

        data["text/plain"] = new Blob([selectedText], { type: "text/plain" });

        const area = JSON.stringify({
            top: parseFloat(highlightPosition.top),
            left: parseFloat(highlightPosition.left),
            isPoint: true,
        });

        data["text/html"] = new Blob(
            [
                `<span class="captis-link" data-project=${document.project} ` +
                    `data-documentId=${document._id} data-area=${area}></span>`,
            ],
            { type: "text/html" },
        );

        try {
            await navigator.clipboard.write([new window.ClipboardItem(data)]);
            notify.info("Link copied to clipboard");
        } catch (error) {
            notify.error("Failed to copy link to clipboard");
        }

        setShowTargetWithActions(false);
    };

    const showHighlightTarget =
        typeof highlightPosition.left === "number" && typeof highlightPosition.top === "number";

    return (
        <div className="image-preview" onClick={onClick}>
            <div className="image-container" style={{ display: "inline-block" }}>
                <img
                    src={format.image(document.file._id)}
                    ref={imgRef}
                    onContextMenu={onRightClick}
                    style={{ display: "block", width: "100%" }}
                />
                {showHighlightTarget && (
                    <HighlightTarget
                        left={highlightPosition.left}
                        top={highlightPosition.top}
                        showActions={showTargetWithActions}
                        onCopyLink={onCopyLink}
                        onAddAsBookmark={onAddAsBookmark}
                        onAddAsComment={onAddAsComment}
                    />
                )}
                <BookmarkModal
                    visible={!!bookmark}
                    bookmark={bookmark}
                    onSave={(data) => {
                        setBookmark(null);
                        onBookmark && onBookmark(data);
                    }}
                    onDiscard={() => {
                        setBookmark(null);
                    }}
                />
                <CommentsModal
                    modalOpen={comment}
                    setModalOpen={() => setComment(null)}
                    onSubmitHandler={(data) => {
                        onComment && onComment({ ...data, ...comment });
                        setComment(null);
                    }}
                />
                <RenderBookmarksTabs bookmarks={bookmarks} document={document} />

                <RenderCommentsTabs comments={comments} document={document} />
            </div>
        </div>
    );
});

export default ImagePreview;
